import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutInfo } from '@/mocks/data';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '国产精品在线',
  url: 'https://www.guochangjingpin.com',
  description: '国产精品在线为您汇聚海量正版高清国产内容，涵盖影视、综艺、纪录片等多元品类。',
  foundingDate: '2018',
  foundingLocation: {
    '@type': 'Place',
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '北京',
      addressCountry: 'CN',
      streetAddress: '北京市朝阳区建国路88号国贸大厦A座',
    },
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+86-400-888-9999',
    contactType: 'customer service',
    email: 'service@guochangjingpin.com',
  },
};

const aboutPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于我们 - 国产精品在线',
  description: '了解国产精品在线的品牌故事、发展历程、荣誉资质及联系方式。',
  url: 'https://www.guochangjingpin.com/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('loading');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, String(value));
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9qqj96e3oq4jqumtov0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const errMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (errMsg.toLowerCase().includes('spam') || errMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(errMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description="了解国产精品在线的品牌故事、发展历程、荣誉资质及联系方式。国产精品在线致力于汇聚优质国产内容，传承中华优秀文化。"
        keywords="关于国产精品在线,国产内容平台,品牌介绍,联系我们"
        canonicalUrl="https://www.guochangjingpin.com/about"
        schemaData={[organizationSchema, aboutPageSchema]}
      />

      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Brand Intro */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              关于国产精品在线
            </h1>
            <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
              {aboutInfo.mission}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-12">
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-4">
                <i className="ri-flag-line text-xl"></i>
              </div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
                我们的使命
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {aboutInfo.mission}
              </p>
            </div>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                <i className="ri-eye-line text-xl"></i>
              </div>
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-3">
                我们的愿景
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed">
                {aboutInfo.vision}
              </p>
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="w-full bg-background-100/70 py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              发展历程
            </h2>
            <div className="max-w-3xl mx-auto">
              <div className="relative pl-8 border-l-2 border-primary-200">
                {aboutInfo.milestones.map((item, index) => (
                  <div key={index} className="mb-6 last:mb-0 relative">
                    <div className="absolute -left-[33px] w-4 h-4 rounded-full bg-primary-500 border-2 border-background-50"></div>
                    <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                      <span className="text-sm font-bold text-primary-600">{item.year}</span>
                      <p className="mt-1 text-sm text-foreground-700 leading-relaxed">{item.event}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Honors */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
            荣誉资质
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {aboutInfo.honors.map((honor, index) => (
              <div
                key={index}
                className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5 text-center hover:border-accent-300 transition-colors"
              >
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className="ri-award-line text-lg"></i>
                </div>
                <p className="text-xs md:text-sm text-foreground-700 font-medium leading-snug">{honor}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section className="w-full bg-background-100/70 py-10 md:py-14" id="contact">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-8">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 max-w-5xl mx-auto">
              {/* Contact Info */}
              <div className="space-y-5">
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.email}</p>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.phone}</p>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.address}</p>
                  </div>
                </div>
                <div className="bg-background-50 rounded-lg border border-background-200/70 p-5 flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信公众号</h3>
                    <p className="text-sm text-foreground-600">{aboutInfo.contact.wechat}</p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">发送消息</h3>
                <form onSubmit={handleSubmit} data-readdy-form="true">
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3.5 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                    </div>

                    <div className="honeypot-wrapper">
                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    {formStatus === 'error' && formError && (
                      <div className="text-sm text-red-600 bg-red-50 px-3.5 py-2.5 rounded-md">
                        {formError}
                      </div>
                    )}
                    {formStatus === 'success' && (
                      <div className="text-sm text-green-700 bg-green-50 px-3.5 py-2.5 rounded-md">
                        消息已成功发送！我们会尽快与您联系，感谢您的关注。
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}